import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query'
import { useApiWithIDToken } from '../../state/modules/common'
import { useNotifications } from '../../state/modules/notifications'
import { Variant } from '../../common/types'
import { useProgressToast } from './helpers/useProgressToast'

type DeleteChecklistDocumentVariables = {
  documentId: number
  documentName: string
}

/**
 * Delete the most recent upload for a given checklist document.
 *
 * @returns A UseMutationResult object for triggering the deletion.
 */
export const useDeleteChecklistDocument = (): UseMutationResult<
  void,
  Error,
  DeleteChecklistDocumentVariables
> => {
  const api = useApiWithIDToken()
  const queryClient = useQueryClient()
  const { send: sendNotification } = useNotifications()
  const { startProgress, endProgress } = useProgressToast()

  return useMutation<
    void,
    Error,
    DeleteChecklistDocumentVariables,
    { toastId: string }
  >({
    mutationFn: ({ documentId, documentName }) =>
      deleteChecklistDocument(api, documentId, documentName),
    onMutate: (vars) => ({
      toastId: startProgress(
        'Deleting Document',
        `Deleting files for ${vars.documentName}`
      ),
    }),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: ['siteRFPChecklists'],
        refetchType: 'active',
      })
      void queryClient.invalidateQueries({
        queryKey: ['siteRFPDocumentSummary'],
        refetchType: 'active',
      })
      sendNotification({
        title: 'Document Deleted',
        message: `${vars.documentName} has been successfully deleted.`,
        useRucksackUI: true,
      })
    },
    onError: (_data, vars) => {
      sendNotification({
        title: 'Delete Failed',
        message: `An error occurred while trying to delete ${vars.documentName}. Please try again.`,
        variant: Variant.DANGER,
        useRucksackUI: true,
      })
    },
    onSettled: (_data, _error, _vars, context) => {
      endProgress(context?.toastId)
    },
  })
}

async function deleteChecklistDocument(
  api: ReturnType<typeof useApiWithIDToken>,
  documentId: number,
  documentName: string
): Promise<void> {
  try {
    const url = `/documents/${documentId}/delete`
    await api.put(url)
  } catch (error) {
    throw new Error(
      `Failed to delete document ${documentName}: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    )
  }
}
